import { ArrowDownIcon, ArrowLeftIcon, ArrowUpIcon, BoltIcon, BoltSlashIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType, setGlobalConfig } from 'actions'
import cloneDeep from 'clone-deep'
import { OptionModal } from 'components/Modals/OptionModal'
import { type InputType, resolveAccountTypeKey, resolveAccountTypeTitle, SettingKey } from 'config'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { submitCondition } from 'services/apis/conditions'
import { svgLoading } from 'stories/assets'
import {
  Button,
  Checkbox,
  InputVisibleLogic,
  Toggle,
  Tooltip,
} from 'stories/components'
import { ISelectOption } from 'stories/components/SelectOptionInputs/types'
import {
  capitalizeFirstLetter,
  confirm,
  convertToSanitize,
  getPartiesMap,
  InputConvert,
  InputValidate,
  isNumValue,
} from 'utils'
import { getOptionValues, IOptionType } from 'utils/general'
import { RenderInput } from 'utils/RenderInput'

import { conditionInputs } from '../constant'
import { defaultChecklistInputs } from './constant'
import type {
  IChecklistItem,
  IChecklistVisibilityOption,
  ICondition,
  IConditionTemplate,
  IReviewActionChain,
} from './types'

const placeholderFields = [
  'description',
  'brokerDescription',
  'borrowerName',
  'borrowerDescription',
  'projectInstructions',
]

export function ConditionDetails(props: {
  templates: IConditionTemplate[]
  defaults: ICondition | null
  isDuplicated: boolean
  lastUpdatedAt: number
  onBack: Function
  onComplete: Function
  categories: string[]
}) {
  const dispatch = useDispatch()

  const [conditionTypeOptions, documentCategoryOptions] = useSelector((state: any) => {
    return [
      state.globalConfigReducer[GlobalConfigType.ConditionTypes],
      state.globalConfigReducer[GlobalConfigType.DocumentCategory],
    ]
  })

  const [isLoading, setLoading] = useState(false)
  const [inputs, setInputs] = useState<Record<string, InputType>>({})
  const [selectedTemplates, setSelectedTemplates] = useState<Array<number> | null>(null)
  const { templates = [], defaults = null, isDuplicated, lastUpdatedAt } = props
  const [isForActiveLoan, setForActiveLoan] = useState(false)
  const [isDeleteForActiveLoan, setDeleteForActiveLoan] = useState(false)
  const [typeSettingModal, setTypeModal] = useState<SettingKey | null>(null)
  const [checklist, setChecklist] = useState<IChecklistItem[]>(defaults?.checklist || [])
  const [error, setError] = useState(false)
  const [cursorPos, setCursorPos] = useState(0)
  const [focusField, setFocusField] = useState('')
  const activeInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)

  const updateInputs = (inputs: Record<string, InputType>) => {
    inputs.reviewActionChains.disabled = !inputs.reviewActionStatus.value
    // Internal conditions are never requested from an outside party.
    if (inputs.requestedTo) {
      const isInternal = !!inputs.intext?.value
      inputs.requestedTo.visible = !isInternal
      if (isInternal) inputs.requestedTo.value = ''
    }
  }

  useEffect(() => {
    if (!defaults) {
      setInputs(conditionInputs(props.categories))
      setSelectedTemplates([])
      return
    }
    const newStats = cloneDeep(conditionInputs(props.categories)) as Record<string, any>
    for (const key in newStats) {
      newStats[key].value = (defaults as any)[key]
    }
    // A role renamed since this condition was saved leaves a title the Select no longer offers.
    newStats.responsibility.value = resolveAccountTypeTitle(defaults.responsibility)
    if (isDuplicated) newStats.name.value = ''
    updateInputs(newStats)
    setInputs(newStats)

    const newTemplates: Array<number> = []
    templates.forEach((template) => {
      if (template.conditions.indexOf(defaults.no) != -1) newTemplates.push(template.no)
    })
    setSelectedTemplates(newTemplates)
  }, [defaults, lastUpdatedAt, templates, isDuplicated])

  const onBack = () => {
    props.onBack()
  }

  const onChange = (key: string, value: any) => {
    let newInputs = cloneDeep(inputs)
    newInputs[key].value = InputConvert(newInputs[key], value)
    newInputs[key].error = ''
    updateInputs(newInputs)
    if (key == 'reviewActionStatus' && value == true && !newInputs.reviewActionChains.value?.length) {
      const conditionName = newInputs.name.value
      newInputs.reviewActionChains.value = [
        {
          key: `${Date.now()}`,
          title: capitalizeFirstLetter(conditionName.toLowerCase()),
        },
      ]
    }
    if (key === 'intext' && value) {
      // Keep "Requested at Application" but never keep an outside party on internal.
      if (newInputs.requestedTo) newInputs.requestedTo.value = ''
    }

    setInputs(newInputs)
  }

  const handleFocus = (event: any, fieldKey: string) => {
    activeInputRef.current = event.target
    setCursorPos(event.target.selectionStart)
    setFocusField(fieldKey)
  }

  const handleCursorChange = (event: any) => {
    setCursorPos(event.target.selectionStart)
  }

  const insertTextAtCursor = (text: string) => {
    const input = activeInputRef.current
    if (!input) return

    const value = input.value
    const newValue = value.substring(0, cursorPos) + text + value.substring(cursorPos)
    const newCursorPos = cursorPos + text.length

    const checklistMatch = focusField.match(/^checklist-(\d+)-title$/)
    if (checklistMatch) {
      onChangeChecklist(parseInt(checklistMatch[1]), 'title', newValue)
    } else {
      onChange(focusField, newValue)
    }

    setTimeout(() => {
      input.focus({ preventScroll: true })
      input.setSelectionRange(newCursorPos, newCursorPos)
      setCursorPos(newCursorPos)
    }, 0)
  }

  const placeholders = getPartiesMap({})

  const changeTempConds = (no: number, value: boolean) => {
    if (!selectedTemplates) return
    const newValues = cloneDeep(selectedTemplates)
    const index = newValues.indexOf(no)
    if (value && index == -1) newValues.push(no)
    else if (!value && index != -1) newValues.splice(index, 1)
    setSelectedTemplates(newValues)
  }

  const onSubmit = async () => {
    let hasError = false
    const newStats = cloneDeep(inputs)
    const data: Record<string, any> = {}
    for (const key in newStats) {
      const { value, disabled = false } = newStats[key]
      let error = InputValidate(newStats[key])
      newStats[key].error = error
      if (error.length > 0) {
        hasError = true
        console.log(key, hasError, error)
      }

      if (!disabled && value !== undefined) data[key] = value
    }
    checklist?.map((config: IChecklistItem) => {
      config.visibleLogic?.map((andLogic: IChecklistVisibilityOption[]) => {
        andLogic.map((logic: IChecklistVisibilityOption) => {
          if (logic.fieldKey === '' || logic.value === '') {
            hasError = true
            console.log('checklist', logic.fieldOrigin, logic.fieldKey, logic.value, hasError, error)
          }
        })
      })
    })
    console.log('hasError', hasError)
    if (hasError) {
      setError(hasError)
      setInputs(newStats)
      return
    }

    if (isForActiveLoan) {
      const isUpdateLoans = await confirm('Do you want to update all active Loans?')
      data.isUpdateLoans = isUpdateLoans
    }

    if (isDeleteForActiveLoan) {
      const isDeleteLoans = await confirm('Do you want to delete all active Loans?')
      data.isDeleteLoans = isDeleteLoans
    }

    setLoading(true)

    data.templates = selectedTemplates

    const id = !defaults || isDuplicated ? 0 : defaults.id
    if (isDuplicated && defaults) {
      delete (defaults as any).id
      delete (defaults as any).no
    }

    data.checklist = checklist

    data.reviewActionChains = ((data.reviewActionChains as ISelectOption[]) || []).map((v) => {
      if (isNumValue(v.key)) v.key = `reviewAction${defaults?.no || 'NEW'}-${convertToSanitize(v.title)}`
      return v
    })
    submitCondition(id, data)
      .then(() => {
        if (id == 0) toast('New condition is added.', { type: 'success' })
        else toast(`Condition No ${defaults?.displayNo} is updated.`, { type: 'success' })

        props.onComplete()
        onBack()
      })
      .catch(() => setLoading(false))
  }

  const additionalFieldOrigins = useMemo(() => {
    const inputs: Record<string, InputType> = {}
    checklist.forEach((item) => {
      inputs[item.key] = {
        inputType: item.inputType as any,
        title: item.title,
      }
    })
    return {
      ChecklistFields: {
        title: 'Checklist Fields',
        inputs,
      },
    }
  }, [checklist])

  const onAddChecklist = () => {
    const temp = cloneDeep(checklist)
    temp.push({
      inputType: '',
      title: '',
      key: `${Date.now()}`,
      eligibleValue: '',
      checkIneligible: false,
      visibleLogic: [],
      responsibility: resolveAccountTypeKey(inputs.responsibility.value) || '',
    })
    setChecklist(temp)
  }

  const onRemoveChecklist = (index: number) => {
    const temp = cloneDeep(checklist)
    temp.splice(index, 1)
    setChecklist(temp)
  }

  const onMoveChecklist = (index: number, dir: 1 | -1) => {
    if (index + dir < 0 || index + dir >= checklist.length) return

    const temp = cloneDeep(checklist)
    const curItem = temp[index]
    temp[index] = temp[index + dir]
    temp[index + dir] = curItem
    setChecklist(temp)
  }

  const onChangeChecklist = (index: number, key: string, value: any) => {
    const temp: any = cloneDeep(checklist)
    temp[index][key] = value
    setChecklist(temp)
  }

  const onChangeChecklistVisibleLogic = (index: number, newValue: any) => {
    const temp = cloneDeep(checklist)
    temp[index].visibleLogic = newValue
    setChecklist(temp)
  }

  const isSelectedAll = useMemo(() => {
    if (!selectedTemplates) return false
    return templates.filter((item) => selectedTemplates.indexOf(item.no) === -1).length == 0
  }, [selectedTemplates, templates])

  return (
    <div className="Conditions-container">
      <h2 className="text-2xl font-bold flex items-center mb-3">
        Conditions - {!defaults || isDuplicated ? 'Add' : `Update No ${defaults.displayNo}`}
        {isLoading && (
          <span className="ml-3">
            <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
          </span>
        )}
      </h2>
      <Button link onClick={onBack}>
        <div className="flex text-shade-blue items-center">
          <ArrowLeftIcon className="w-4 h-4 mr-2" /> <p>Return to Conditions</p>
        </div>
      </Button>

      <div className="my-4">
        <div className="border-b mb-2">- Placeholders</div>
        <pre className="flex gap-1 flex-wrap">
          {Object.keys(placeholders)
            .sort()
            .map((key, index) => (
              <div
                key={index}
                className="mr-2 cursor-pointer hover:underline"
                onClick={() => insertTextAtCursor(`{{${key}}}`)}
              >
                {key},{' '}
              </div>
            ))}
        </pre>
      </div>

      <div className="grid gap-4 md:grid-cols-2 grid-cols-1 mb-3">
        {Object.keys(inputs).map((key, index) => {
          const input = inputs[key]
          if (input.visible === false) return null
          if (key == 'type') {
            input.additionalElements = (
              <span className="btn-link" onClick={() => setTypeModal(SettingKey.CONDITION_TYPE_OPTIONS)}>
                Update Types
              </span>
            )
            ;(input as any).options = getOptionValues(conditionTypeOptions)
          }
          if (key == 'documentCategory') {
            ;(input as any).options = getOptionValues(documentCategoryOptions)
          }
          if (key === 'showExpirationDates' && !inputs.checklistEnabled?.value) return

          if (key == 'reviewActionChains')
            input.additionalElements = ((opt: IReviewActionChain, onChange: Function) => {
              const Icon = !opt.repeat ? BoltIcon : BoltSlashIcon
              return (
                <Tooltip message={!opt.repeat ? 'Always' : 'Once'}>
                  <Icon
                    className="w-4 h-4 cursor-pointer text-shade-blue"
                    onClick={() => onChange({ repeat: !opt.repeat })}
                  />
                </Tooltip>
              )
            }) as any

          return (
            <div className={`input ${input.span ? `md:col-span-${input.span}` : ''}`} key={index}>
              <RenderInput
                input={input}
                Key={key}
                onChange={onChange}
                {...(placeholderFields.includes(key)
                  ? {
                      onFocus: (e: any) => handleFocus(e, key),
                      onKeyUp: handleCursorChange,
                      onClick: handleCursorChange,
                    }
                  : {})}
              />
            </div>
          )
        })}
      </div>

      {inputs.checklistEnabled?.value && (
        <>
          <div className="grid grid-cols-2 gap-4">
            {checklist?.map((item, index: number) => {
              let rtl: any[] = []

              rtl.push(
                <div key={`checklist-header-${index}`} className="col-span-2 mb-2">
                  <div className={`border-b-2 border-shade-blue w-full italic flex justify-between items-center py-2`}>
                    <p className="font-semibold">Checklist Item {index + 1}</p>
                    <div className="flex items-center gap-2">
                      <p className="btn-icon" onClick={() => onMoveChecklist(index, -1)}>
                        <ArrowUpIcon className="w-4 h-4" />
                      </p>
                      <p className="btn-icon" onClick={() => onMoveChecklist(index, 1)}>
                        <ArrowDownIcon className="w-4 h-4" />
                      </p>
                      <p
                        className="text-sm cursor-pointer text-red-500 hover:underline"
                        onClick={() => onRemoveChecklist(index)}
                      >
                        Remove
                      </p>
                    </div>
                  </div>
                </div>,
              )

              rtl.push(
                <div key={`checklist-visible-${index}`}>
                  <InputVisibleLogic
                    id={`checklist-visible-logic-${index}`}
                    value={item.visibleLogic as any}
                    options={{ additionalFieldOrigins }}
                    zIndex={0}
                    onChange={(newValue) => onChangeChecklistVisibleLogic(index, newValue)}
                  />
                </div>,
              )
              const checklistInputs = defaultChecklistInputs()
              const temp = Object.keys(checklistInputs).map((key) => {
                const input = cloneDeep(checklistInputs[key])
                input.value = (item as any)[key]
                if (key === 'responsibility' && input.value === undefined) {
                  input.value = resolveAccountTypeKey(inputs.responsibility.value)
                }

                return (
                  <div className={`col-span-${input.span} mt-2`} key={`${index}${key}`}>
                    <RenderInput
                      input={input}
                      Key={`${index}-${key}`}
                      onChange={(_key: string, value: any) => onChangeChecklist(index, key, value)}
                      {...(key === 'title'
                        ? {
                            onFocus: (e: any) => handleFocus(e, `checklist-${index}-title`),
                            onKeyUp: handleCursorChange,
                            onClick: handleCursorChange,
                          }
                        : {})}
                    />
                  </div>
                )
              })

              if (item.checkIneligible) {
                temp.push(
                  <div key={`${index}eligibleValue`} className="mt-2">
                    <RenderInput
                      input={
                        {
                          inputType: item.inputType,
                          title: 'Eligible Value',
                          value: item.eligibleValue,
                        } as any
                      }
                      Key={`${index}-eligibleValue`}
                      onChange={(_key: string, value: any) => onChangeChecklist(index, 'eligibleValue', value)}
                    />
                  </div>,
                )
              }
              rtl = [...rtl, <div key={`checklist-inputs-${index}`}>{temp}</div>]
              return rtl
            })}
          </div>
          <div className="my-4">
            <span className="text-shade-blue cursor-pointer hover:underline" onClick={onAddChecklist}>
              + Add Checklist Item
            </span>
          </div>
        </>
      )}

      <div className="mb-4">
        <div className="flex items-center gap-4 mb-3 border-b">
          <h2 className="text-md font-bold flex items-center">Templates</h2>

          <Toggle
            id={`template-all`}
            title={`Select All`}
            key={`${lastUpdatedAt}-template-check-all`}
            value={isSelectedAll}
            onChange={() => setSelectedTemplates(isSelectedAll ? [] : templates.map((v) => v.no))}
          />
        </div>
        <div className="grid md:grid-cols-2 mb-5 gap-3">
          {selectedTemplates &&
            templates.map((item, index: number) => (
              <div className="col-12 col-md-6" key={`${lastUpdatedAt}-template-${index}`}>
                <Toggle
                  id={`${item.no}`}
                  title={`${item.no} - ${item.name}`}
                  key={`${lastUpdatedAt}-template-check-${index}`}
                  value={selectedTemplates.indexOf(item.no) !== -1}
                  onChange={(value) => changeTempConds(item.no, value)}
                />
              </div>
            ))}
        </div>
        <Checkbox
          id="initializeForActiveLoan"
          title="We need to initialize for all active loans"
          value={isForActiveLoan}
          onChange={(v) => setForActiveLoan(v)}
        />
        <div className="mt-4">
          <Checkbox
            id="deleteForActiveLoan"
            title="We need to delete for all active loans"
            value={isDeleteForActiveLoan}
            onChange={(v) => setDeleteForActiveLoan(v)}
          />
        </div>
      </div>

      <div className="block text-center">
        <Button onClick={onSubmit} className="px-10" loading={isLoading}>
          <>{!defaults || isDuplicated ? 'Add' : 'Update'} Condition</>
        </Button>
      </div>

      {typeSettingModal && (
        <OptionModal
          title="Update Condition Type options"
          settingKey={typeSettingModal}
          onClose={(values: IOptionType[]) => {
            values && dispatch(setGlobalConfig(GlobalConfigType.ConditionTypes, values))
            setTypeModal(null)
          }}
        />
      )}
    </div>
  )
}
